// packages/web-ui/src/components/tutorial/TutorialTour.tsx
//
// React-joyride spotlight tour that walks the user through the core TESSA
// Lean flow: Chat composer → KB change tree (conceptual) → KB browser →
// Help button. Mounted at app root; rendering is gated on
// `useTutorialStore.tourRunning`.
//
// Step targets are matched by `data-testid` on the existing components so we
// don't introduce new selector surface area. The single non-DOM-anchored
// step (the change-tree concept) uses `target: 'body'` + `placement: 'center'`
// because no real changeset is guaranteed to be on screen when the tour runs.

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Joyride, EVENTS, type EventData, type Step } from 'react-joyride';
import { useTutorialStore } from '../../state/tutorial.js';
import { navigateTo } from '../../router/hash-router.js';

/** Selector the first step depends on — we wait for it to appear in the DOM
 *  before starting Joyride so the tour doesn't race the chat-route mount. */
const FIRST_STEP_SELECTOR = '[data-testid="composer"]';
/** Hard cap on how long we wait for the composer. After this the tour starts
 *  anyway so the user isn't stuck behind a silent timeout. */
const TARGET_WAIT_MAX_MS = 5000;
/** Poll interval while waiting for the target. */
const TARGET_WAIT_TICK_MS = 100;

const TOUR_STEPS: ReadonlyArray<Step> = [
  {
    target: '[data-testid="composer"]',
    title: 'Send a request',
    content:
      'Type what you want to change in your knowledge base here. The assistant proposes per-entity SysML v2 changes for you to review.',
    placement: 'top',
    skipBeacon: true,
  },
  {
    target: 'body',
    title: 'Review proposed changes',
    content:
      'When the assistant proposes changes, they appear above the composer grouped by entity (parts, ports, connections). Accept or reject each one — only accepted entities are written to disk.',
    placement: 'center',
    skipBeacon: true,
  },
  {
    target: '[data-testid="activity-bar-kb"]',
    title: 'Browse the knowledge base',
    content:
      'Open the KB browser to explore existing entities in your model. Use it to inspect what is there before asking for changes.',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '[data-testid="help-popover-trigger"]',
    title: 'Help is always here',
    content:
      'Click the "?" button (or press F1) to open the full user guide — example prompts, what each panel does, keyboard shortcuts, and a button to restart this tour.',
    placement: 'right',
    skipBeacon: true,
  },
];

export function TutorialTour(): JSX.Element | null {
  const running = useTutorialStore((s) => s.tourRunning);
  const stopTour = useTutorialStore((s) => s.stopTour);
  const setStep = useTutorialStore((s) => s.setStep);

  // Two-phase mount fix: when the tour starts we (1) navigate to chat and
  // (2) wait for the composer testid to actually appear in the DOM before
  // rendering Joyride. Without phase 2, Joyride mounted immediately, saw no
  // target, fired TARGET_NOT_FOUND, and the tour stopped before any tooltip
  // showed — the user only saw the chat-panel loading state and assumed the
  // tutorial broke.
  const [targetReady, setTargetReady] = useState(false);

  useEffect(() => {
    if (!running) {
      setTargetReady(false);
      return;
    }
    // Phase 1: switch route so the composer mounts.
    navigateTo({ kind: 'chat' });

    // Phase 2: poll for the composer. Bail out after TARGET_WAIT_MAX_MS so
    // even if the composer never shows (unexpected) we still let Joyride try
    // — better a missing-target warning than a silent dead tour.
    if (typeof document === 'undefined') return;
    if (document.querySelector(FIRST_STEP_SELECTOR) !== null) {
      setTargetReady(true);
      return;
    }
    let cancelled = false;
    const start = Date.now();
    const tick = (): void => {
      if (cancelled) return;
      if (document.querySelector(FIRST_STEP_SELECTOR) !== null) {
        setTargetReady(true);
        return;
      }
      if (Date.now() - start >= TARGET_WAIT_MAX_MS) {
        setTargetReady(true);
        return;
      }
      window.setTimeout(tick, TARGET_WAIT_TICK_MS);
    };
    window.setTimeout(tick, TARGET_WAIT_TICK_MS);
    return (): void => {
      cancelled = true;
    };
  }, [running]);

  if (!running) return null;
  if (!targetReady) return null;

  function onEvent(data: EventData): void {
    if (data.action === 'update' && data.lifecycle === 'tooltip') {
      setStep(data.index);
    }
    // Note: TARGET_NOT_FOUND deliberately NOT in this list. Joyride retries
    // missing targets internally; a transient miss (e.g. KB activity-bar
    // button rendered a frame late) should NOT kill the whole tour. We only
    // stop on explicit user actions or a clean end-of-tour.
    if (
      data.type === EVENTS.TOUR_END ||
      data.action === 'close' ||
      data.action === 'skip' ||
      data.status === 'finished' ||
      data.status === 'skipped'
    ) {
      stopTour();
    }
  }

  return (
    <Joyride
      steps={TOUR_STEPS as Step[]}
      run={running}
      continuous
      onEvent={onEvent}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip',
      }}
      options={{
        primaryColor: '#bb0a30',
        textColor: '#1a1a1a',
        backgroundColor: '#fbf7f0',
        zIndex: 60,
        showProgress: true,
        buttons: ['back', 'skip', 'primary'],
        overlayClickAction: false,
      }}
    />
  );
}
